import { GroupingOption } from '@features/purchasing/types/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Define the available purchasing views
export type PurchasingView = 'timeline' | 'board' | 'list' | 'drafts';


// Define filter types for different features
export interface FilterState {
  [key: string]: any;
}

// Define the main UI state interface
interface UIState {
  // Purchasing view preferences
  purchasingView: PurchasingView;

  // Purchasing grouping preference
  purchasingGrouping: GroupingOption;

  // Filter states for different features
  filters: {
    purchasing: FilterState;
    inventory: FilterState;
    products: FilterState;
    sales: FilterState;
    [key: string]: FilterState;
  };

  // Sidebar and drawer states
  sidebar: {
    isOpen: boolean;
    width: number;
  };

  // Table preferences
  tablePreferences: {
    [key: string]: {
      pageSize: number;
      sortBy: string;
      sortDirection: 'asc' | 'desc';
      columns: string[];
    };
  };

  // Actions
  setPurchasingView: (view: PurchasingView) => void;
  setPurchasingGrouping: (grouping: GroupingOption) => void;
  setFilter: (feature: string, filterKey: string, value: any) => void;
  clearFilters: (feature: string) => void;
  setSidebarOpen: (isOpen: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setTablePreference: (tableId: string, preference: Partial<{
    pageSize: number;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    columns: string[];
  }>) => void;
  resetUIState: () => void;
}

// Default values
const defaultFilters: FilterState = {};
const defaultSidebar = { isOpen: true, width: 280 };
const defaultTablePreferences = {};

export const useUIStateStore = create(
  persist<UIState>(
    (set) => ({
      // Initial state
      purchasingView: 'timeline',
      purchasingGrouping: GroupingOption.PURCHASE_ORDER, // Default grouping
      filters: {
        purchasing: { ...defaultFilters },
        inventory: { ...defaultFilters },
        products: { ...defaultFilters },
        sales: { ...defaultFilters },
        dashboard: { salesKpiRangeOption: 'ytd' },
      },
      sidebar: { ...defaultSidebar },
      tablePreferences: { ...defaultTablePreferences },

      // Actions
      setPurchasingView: (view: PurchasingView) =>
        set({ purchasingView: view }),

      setPurchasingGrouping: (grouping: GroupingOption) =>
        set({ purchasingGrouping: grouping }),

      setFilter: (feature: string, filterKey: string, value: any) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [feature]: {
              ...state.filters[feature],
              [filterKey]: value,
            },
          },
        })),

      clearFilters: (feature: string) =>
        set((state) => ({
          filters: {
            ...state.filters,
            [feature]: { ...defaultFilters },
          },
        })),

      setSidebarOpen: (isOpen: boolean) =>
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            isOpen,
          },
        })),

      setSidebarWidth: (width: number) =>
        set((state) => ({
          sidebar: {
            ...state.sidebar,
            width,
          },
        })),

      setTablePreference: (tableId: string, preference: Partial<{
        pageSize: number;
        sortBy: string;
        sortDirection: 'asc' | 'desc';
        columns: string[];
      }>) =>
        set((state) => ({
          tablePreferences: {
            ...state.tablePreferences,
            [tableId]: {
              ...state.tablePreferences[tableId],
              ...preference,
            },
          },
        })),

      resetUIState: () =>
        set((state) => ({
          ...state,
          purchasingView: 'timeline',
          purchasingGrouping: GroupingOption.PURCHASE_ORDER,
          filters: {
            purchasing: { ...defaultFilters },
            inventory: { ...defaultFilters },
            products: { ...defaultFilters },
            sales: { ...defaultFilters },
            dashboard: { salesKpiRangeOption: 'ytd' },
          },
          sidebar: { ...defaultSidebar },
          tablePreferences: { ...defaultTablePreferences },
        })),
    }),
    {
      name: 'ui-state-storage'
    }
  )
);

// Convenience hooks for specific features
export const usePurchasingUIState = () => {
  const { purchasingView, setPurchasingView, purchasingGrouping, setPurchasingGrouping, filters, setFilter, clearFilters } = useUIStateStore();

  return {
    currentView: purchasingView,
    setView: setPurchasingView,
    grouping: purchasingGrouping,
    setGrouping: setPurchasingGrouping,
    filters: filters.purchasing,
    setFilter: (key: string, value: any) => setFilter('purchasing', key, value),
    clearFilters: () => clearFilters('purchasing'),
  };
};

export const useInventoryUIState = () => {
  const { filters, setFilter, clearFilters } = useUIStateStore();

  return {
    filters: filters.inventory,
    setFilter: (key: string, value: any) => setFilter('inventory', key, value),
    clearFilters: () => clearFilters('inventory'),
  };
};

export const useProductsUIState = () => {
  const { filters, setFilter, clearFilters } = useUIStateStore();

  return {
    filters: filters.products,
    setFilter: (key: string, value: any) => setFilter('products', key, value),
    clearFilters: () => clearFilters('products'),
  };
};

export const useSalesUIState = () => {
  const { filters, setFilter, clearFilters } = useUIStateStore();

  return {
    filters: filters.sales,
    setFilter: (key: string, value: any) => setFilter('sales', key, value),
    clearFilters: () => clearFilters('sales'),
  };
};

export const useSidebarState = () => {
  const { sidebar, setSidebarOpen, setSidebarWidth } = useUIStateStore();

  return {
    isOpen: sidebar.isOpen,
    width: sidebar.width,
    setOpen: setSidebarOpen,
    setWidth: setSidebarWidth,
  };
};

export const useTablePreferences = (tableId: string) => {
  const { tablePreferences, setTablePreference } = useUIStateStore();

  return {
    preferences: tablePreferences[tableId] || {},
    setPreference: (preference: Partial<{
      pageSize: number;
      sortBy: string;
      sortDirection: 'asc' | 'desc';
      columns: string[];
    }>) => setTablePreference(tableId, preference),
  };
};
